import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, TouchableOpacity } from 'react-native';
import { BasicLayout } from '../../../common/components/CustomLayout';
import { BOHToolbar } from '../../../common/components/bohtoolbar';
import { getAPICall } from '../../../api/BaseAPI';
import { getBrandContentById } from '../../../api/Settings';
import BrandContent from '../storedetails/brandContent';
import { Setting } from '../storedetails/brandContent/ContentField';
import ContentBlockModal, { ContentBlock } from './ContentBlockModal';
import ContentBlocksTable from './ContentBlocksTable';
import StoreEditMode from './editmode/StoreEditMode';

interface Props {
  navigation: any;
  goBack?: () => void;
  initialBrandId?: string;
}

type CmsView = 'content' | 'blocks' | 'edit';

/**
 * Unified CMS editor (Option B). Renders the structured, typed BrandContent
 * editor (System B: setting_content_blocks + setting_store_contents) with a
 * brand selector, plus a "Content Blocks" view to manage the block
 * definitions themselves. Replaces the raw key/value ManageKeys table.
 */
const CMSContent = ({ navigation, goBack, initialBrandId = '' }: Props) => {
  const [view, setView] = useState<CmsView>('content');
  const [brands, setBrands] = useState<any[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>(initialBrandId);
  const [fields, setFields] = useState<Setting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);
  const [editingBlock, setEditingBlock] = useState<ContentBlock | Setting | null>(null);
  const [blocksRefresh, setBlocksRefresh] = useState(0);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) fetchBrandContent(selectedBrand);
    else setFields([]);
  }, [selectedBrand]);

  const fetchBrands = async () => {
    await getAPICall('price/getbrandsdata', (jsonRes: any, status: number) => {
      if (status === 200) {
        const list = Array.isArray(jsonRes) ? jsonRes : jsonRes?.data || [];
        setBrands(list);
        // Auto-select the first brand if none provided.
        if (!selectedBrand && list.length > 0) setSelectedBrand(String(list[0].id));
      }
    });
  };

  const fetchBrandContent = async (brandId: string) => {
    setIsLoading(true);
    await getBrandContentById(brandId, (jsonRes: any, status: number) => {
      setFields(status === 200 && Array.isArray(jsonRes) ? jsonRes : []);
      setIsLoading(false);
    });
  };

  const openAddBlock = () => {
    setEditingBlock(null);
    setIsBlockModalVisible(true);
  };

  const openEditBlock = (block: ContentBlock) => {
    setEditingBlock(block);
    setIsBlockModalVisible(true);
  };

  const handleBlocksChanged = () => {
    setBlocksRefresh((n) => n + 1);
    if (selectedBrand) fetchBrandContent(selectedBrand);
  };

  const selectedBrandName = brands.find((b: any) => String(b.id) === selectedBrand)?.brand || '';

  if (view === 'edit' && selectedBrand) {
    return (
      <BasicLayout navigation={navigation} goBack={goBack} screenName={'CMS Settings'}>
        <StoreEditMode
          brandId={Number(selectedBrand)}
          brandName={selectedBrandName}
          onClose={() => { setView('content'); fetchBrandContent(selectedBrand); }}
        />
      </BasicLayout>
    );
  }

  return (
    <BasicLayout navigation={navigation} goBack={goBack} screenName={'CMS Settings'}>
      <BOHToolbar>
        <View style={styles.viewToggle}>
          {([['content', 'Brand Content'], ['blocks', 'Content Blocks']] as [CmsView, string][]).map(([key, label]) => (
            <TouchableOpacity
              key={key}
              style={[styles.viewToggleBtn, view === key && styles.viewToggleBtnActive]}
              onPress={() => setView(key)}
            >
              <Text style={[styles.viewToggleText, view === key && styles.viewToggleTextActive]}>{label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {view === 'content' && (
          <>
            <View style={styles.filterContainer}>
              <Text style={styles.filterLabel}>Brand:</Text>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                style={styles.filterSelect as any}
              >
                <option value="">Select a brand…</option>
                {brands.map((b: any) => (
                  <option key={b.id} value={String(b.id)}>{b.brand}</option>
                ))}
              </select>
            </View>
            <TouchableOpacity
              style={[styles.editStoreButton, !selectedBrand && { opacity: 0.5 }]}
              disabled={!selectedBrand}
              onPress={() => setView('edit')}
            >
              <Text style={styles.addBlockButtonText}>Edit Storefront</Text>
            </TouchableOpacity>
          </>
        )}
        <TouchableOpacity style={styles.addBlockButton} onPress={openAddBlock}>
          <Text style={styles.addBlockButtonText}>+ Add Content Block</Text>
        </TouchableOpacity>
      </BOHToolbar>

      {view === 'blocks' ? (
        <ContentBlocksTable onEdit={openEditBlock} refreshTrigger={blocksRefresh} onChanged={handleBlocksChanged} />
      ) : (
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.body}>
            {isLoading ? (
              <ActivityIndicator size="large" style={{ marginTop: 40 }} />
            ) : !selectedBrand ? (
              <Text style={styles.hint}>Select a brand to edit its content.</Text>
            ) : fields.length === 0 ? (
              <Text style={styles.hint}>No content blocks found for this brand.</Text>
            ) : (
              <BrandContent brandId={Number(selectedBrand)} fields={fields} />
            )}
          </View>
        </ScrollView>
      )}

      <ContentBlockModal
        isModalVisible={isBlockModalVisible}
        closeModal={() => setIsBlockModalVisible(false)}
        onSaved={handleBlocksChanged}
        block={editingBlock}
      />
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  viewToggle: {
    flexDirection: 'row',
    marginLeft: 15,
    borderWidth: 1,
    borderColor: '#2e96e1',
    borderRadius: 5,
    overflow: 'hidden',
  },
  viewToggleBtn: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    backgroundColor: '#fff',
  },
  viewToggleBtnActive: {
    backgroundColor: '#2e96e1',
  },
  viewToggleText: {
    fontSize: 14,
    color: '#2e96e1',
    fontWeight: '600',
  },
  viewToggleTextActive: {
    color: '#fff',
  },
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 15,
    gap: 8,
  },
  editStoreButton: {
    marginLeft: 12,
    backgroundColor: '#16a34a',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 5,
  },
  addBlockButton: {
    marginLeft: 'auto',
    marginRight: 15,
    backgroundColor: '#2e96e1',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 5,
  },
  addBlockButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterSelect: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 8,
    fontSize: 14,
    minWidth: 220,
    height: 40,
  },
  scroll: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'flex-start',
  },
  body: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    maxWidth: 900,
    width: '100%',
  },
  hint: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 40,
    textAlign: 'center',
  },
});

export default CMSContent;
